import { useMutation, useQuery } from "@apollo/client";
import {
  CheckBadgeIcon,
  CheckIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";
import {
  MediaCard,
  MediaFrame,
  MediaGrid,
  MediaImage,
  MediaTop,
} from "../../components/Styled/Media";
import { PhotoVerification } from "../../graphql/graphql";
import { APPROVE_PHOTO_VERIFICATION } from "../../graphql/mutations-gql";
import { PHOTO_VERIFICATIONS } from "../../graphql/queries-gql";

const Pair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: ${(props) => props.theme.border};
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const PhotoVerify = () => {
  const { data, loading, error } = useQuery(PHOTO_VERIFICATIONS);
  const [approvePhotoVerification] = useMutation(APPROVE_PHOTO_VERIFICATION);
  const navigate = useNavigate();

  const verifications: PhotoVerification[] = data?.photoVerifications || [];

  const handleApprove = (value: boolean, userId: string) => {
    approvePhotoVerification({
      variables: {
        userId,
        value,
      },
      onCompleted: (data) => {
        console.log("approvePhotoVerification onCompleted", data);
      },
      onError: (error) => {
        console.log("approvePhotoVerification onError", error);
      },
      refetchQueries: [PHOTO_VERIFICATIONS],
    });
  };

  const showImage = (src: string) => {
    window.open(src, "_blank");
  };

  return (
    <Page
      title="Photo verify"
      description="Check that the verification selfie shows the same person as the profile photo."
      badge={
        verifications.length > 0 && (
          <Badge $tone="primary">{verifications.length} waiting</Badge>
        )
      }
    >
      {loading && <Loader />}
      {error && <ErrorAlert error={error} />}

      {!loading && !error && verifications.length === 0 && (
        <EmptyState
          Icon={CheckBadgeIcon}
          title="Nothing to verify"
          description="New verification selfies show up here."
        />
      )}

      <MediaGrid $min={340}>
        {verifications.map((v) => (
          <MediaCard key={v.profilePhoto?.uploadId}>
            <Pair>
              <MediaFrame
                $ratio="9 / 16"
                onClick={() => showImage(v.profilePhoto?.url ?? "")}
              >
                <MediaImage src={v.profilePhoto?.thumbnailUrl ?? ""} alt="" />
                <MediaTop>
                  <Badge $solid>Profile</Badge>
                </MediaTop>
              </MediaFrame>
              <MediaFrame
                $ratio="9 / 16"
                onClick={() => showImage(v.verifyPhoto?.url ?? "")}
              >
                <MediaImage src={v.verifyPhoto?.thumbnailUrl ?? ""} alt="" />
                <MediaTop>
                  <Badge $solid>Selfie</Badge>
                </MediaTop>
              </MediaFrame>
            </Pair>
            <CardFooter>
              <Button
                size="small"
                color="inherit"
                startIcon={<UserIcon width={15} />}
                onClick={() => navigate(`/user/${v.userId}`)}
              >
                Open user
              </Button>
              <Spacer />
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<XMarkIcon width={15} />}
                onClick={() => handleApprove(false, v.userId)}
              >
                Reject
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckIcon width={15} />}
                onClick={() => handleApprove(true, v.userId)}
              >
                Approve
              </Button>
            </CardFooter>
          </MediaCard>
        ))}
      </MediaGrid>
    </Page>
  );
};
export default PhotoVerify;
